
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { JobApplication } from "@/types/application";

async function fetchJobApplications(jobId: string, userId: string): Promise<JobApplication[]> {
  if (!jobId || !userId) {
    console.log("fetchJobApplications: jobId or userId is undefined, returning empty array.");
    return [];
  }
  console.log(`fetchJobApplications: Fetching applications for job ID: ${jobId}, user: ${userId}`);

  // First verify the job belongs to the current user
  const { data: jobData, error: jobError } = await supabase
    .from("jobs")
    .select("id, user_id")
    .eq("id", jobId)
    .eq("user_id", userId) // Ensure user owns the job
    .single();

  if (jobError || !jobData) {
    console.error("Error verifying job ownership:", jobError?.message);
    throw new Error(`Job not found or access denied: ${jobError?.message}`);
  }

  const { data, error } = await supabase
    .from("job_applications")
    .select(`
      id,
      job_id,
      candidate_id,
      status,
      applied_at,
      notes,
      created_at,
      updated_at,
      candidates (
        id,
        first_name,
        last_name,
        email,
        phone,
        user_id
      )
    `)
    .eq("job_id", jobId)
    .order("applied_at", { ascending: false });

  if (error) {
    console.error("Error fetching job applications:", error.message);
    throw new Error(`Failed to fetch job applications: ${error.message}`);
  }

  // Filter applications to only include candidates owned by the current user
  const filteredData = (data || []).filter(app => 
    app.candidates && app.candidates.user_id === userId
  );

  console.log(`fetchJobApplications: Successfully fetched ${filteredData.length} applications.`);
  
  return filteredData.map(app => ({
    ...app,
    candidate: app.candidates
  })) as JobApplication[];
}

export function useJobApplications(jobId: string) {
  const { session } = useAuth();

  return useQuery<JobApplication[], Error>({
    queryKey: ["jobApplications", jobId, session?.user?.id],
    queryFn: () => fetchJobApplications(jobId, session?.user?.id || ''),
    enabled: !!jobId && !!session?.user?.id,
    retry: 1,
  });
}
